import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { InvestmentCalculator } from '@/lib/investment-calculator'
import { useUser } from '@/lib/hooks'
import type { SecondaryTransactionFormData } from '@/types/investment'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Slider } from '@/components/ui/slider'
import { toast } from 'sonner'
import { 
  Calculator, 
  TrendingDown,
  DollarSign,
  AlertCircle,
  Info
} from 'lucide-react'

interface SecondaryTransactionFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  fundingRoundId: string
  projectId: string
  pricePerShare: number
  onSuccess?: () => void
}

export default function SecondaryTransactionForm({
  open,
  onOpenChange,
  fundingRoundId,
  projectId,
  pricePerShare,
  onSuccess
}: SecondaryTransactionFormProps) {
  const queryClient = useQueryClient()
  const { profile: user } = useUser()
  const [calculation, setCalculation] = useState<any>(null)
  const [calculating, setCalculating] = useState(false)

  const [formData, setFormData] = useState<SecondaryTransactionFormData>({
    seller_id: user?.id || '',
    shares_to_sell: 0,
    price_per_share: pricePerShare,
    equity_class_id: '',
    tax_withholding_rate: 20, // Default 20% withholding
    notes: ''
  })

  // Fetch user's equity holdings
  const { data: equityHoldings } = useQuery({
    queryKey: ['user-equity-holdings', user?.id, projectId],
    queryFn: async () => {
      if (!user) return []

      const { data, error } = await supabase
        .from('equity_offers')
        .select(`
          *,
          equity_classes (*)
        `)
        .eq('user_id', user.id)
        .eq('project_id', projectId)
        .eq('status', 'accepted')

      if (error) throw error
      return data || []
    },
    enabled: !!user
  })

  // Set default equity class
  useEffect(() => {
    if (equityHoldings && equityHoldings.length > 0 && !formData.equity_class_id) {
      setFormData(prev => ({
        ...prev,
        equity_class_id: equityHoldings[0].equity_class_id
      }))
    }
  }, [equityHoldings])

  // Calculate transaction details when values change
  useEffect(() => {
    const calculateDetails = async () => {
      if (formData.shares_to_sell > 0 && formData.equity_class_id && user) {
        setCalculating(true)
        try {
          const details = await InvestmentCalculator.calculateSecondaryTransaction(
            user.id,
            formData.equity_class_id,
            formData.shares_to_sell,
            formData.price_per_share,
            formData.tax_withholding_rate
          )
          setCalculation(details)
        } catch (error) {
          console.error('Error calculating transaction details:', error)
          setCalculation(null)
        } finally {
          setCalculating(false)
        }
      } else {
        setCalculation(null)
      }
    }

    const debounceTimer = setTimeout(calculateDetails, 500)
    return () => clearTimeout(debounceTimer)
  }, [
    formData.shares_to_sell, 
    formData.equity_class_id, 
    formData.price_per_share,
    formData.tax_withholding_rate,
    user
  ])

  // Create secondary transaction mutation
  const { mutate: createTransaction, isPending } = useMutation({
    mutationFn: async (data: SecondaryTransactionFormData) => {
      if (!calculation?.canProceed) {
        throw new Error(calculation?.message || 'Cannot proceed with transaction')
      }

      const { error } = await supabase
        .from('secondary_transactions')
        .insert({
          funding_round_id: fundingRoundId,
          seller_id: data.seller_id,
          shares_sold: data.shares_to_sell,
          price_per_share: data.price_per_share,
          total_sale_amount: calculation.totalSaleAmount,
          equity_class_id: data.equity_class_id,
          tax_withholding_amount: calculation.taxWithholding,
          net_proceeds: calculation.netProceeds,
          transaction_status: 'pending', // Requires admin approval
          notes: data.notes
        })

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['secondary-transactions', fundingRoundId] })
      queryClient.invalidateQueries({ queryKey: ['user-equity-holdings'] })
      toast.success('Secondary sale request submitted for approval')
      onOpenChange(false)
      onSuccess?.()
    },
    onError: (error: Error) => {
      toast.error(error.message)
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createTransaction(formData)
  }

  const updateFormData = (updates: Partial<SecondaryTransactionFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }))
  }

  // Calculate percentage of holdings being sold
  const getSellingPercentage = () => {
    if (!calculation?.availableShares || !formData.shares_to_sell) return 0
    return (formData.shares_to_sell / calculation.availableShares) * 100
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Request Secondary Sale</DialogTitle>
            <DialogDescription>
              Sell your vested shares in this funding round. This request requires admin approval.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Equity Class Selection */}
            <div className="space-y-2">
              <Label htmlFor="equity_class">Select Equity Class to Sell</Label>
              <Select
                value={formData.equity_class_id.toString()}
                onValueChange={(value) => updateFormData({
                  equity_class_id: value,
                  shares_to_sell: 0 // Reset shares when class changes
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select equity class" />
                </SelectTrigger>
                <SelectContent>
                  {equityHoldings?.map((holding) => (
                    <SelectItem key={holding.equity_class_id} value={holding.equity_class_id}>
                      {holding.equity_classes?.class_name || 'Unknown'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Available Shares Info */}
            {calculation && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-1">
                    <div>Available vested shares: <strong>{calculation.availableShares.toLocaleString()}</strong></div>
                    <div className="text-sm text-gray-600">
                      You can sell up to {InvestmentCalculator.formatPercentage(100)} of your vested shares
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Shares to Sell */}
            <div className="space-y-2">
              <Label htmlFor="shares_to_sell">Number of Shares to Sell</Label>
              <Input
                id="shares_to_sell"
                type="number"
                min="0"
                max={calculation?.availableShares || 0}
                value={formData.shares_to_sell || ''}
                onChange={(e) => updateFormData({ 
                  shares_to_sell: parseInt(e.target.value) || 0 
                })}
                placeholder="0"
                required
              />
              {calculation && formData.shares_to_sell > 0 && (
                <p className="text-sm text-gray-600">
                  Selling {InvestmentCalculator.formatPercentage(getSellingPercentage())} of your vested shares
                </p>
              )}
            </div>

            {/* Price per Share (Read-only) */}
            <div className="space-y-2">
              <Label htmlFor="price_per_share">Price per Share</Label>
              <Input
                id="price_per_share"
                type="number"
                value={formData.price_per_share}
                disabled
                className="bg-gray-50"
              />
              <p className="text-sm text-gray-600">
                Price is set by the funding round terms
              </p>
            </div>

            {/* Tax Withholding Rate */}
            <div className="space-y-2">
              <Label htmlFor="tax_rate">Tax Withholding Rate: {formData.tax_withholding_rate}%</Label>
              <Slider
                id="tax_rate"
                min={0}
                max={40}
                step={1}
                value={[formData.tax_withholding_rate]}
                onValueChange={(value) => updateFormData({ tax_withholding_rate: value[0] })}
                className="w-full"
              />
              <p className="text-sm text-gray-600">
                Estimated tax withholding (consult your tax advisor)
              </p>
            </div>

            {/* Transaction Summary */}
            {calculation && formData.shares_to_sell > 0 && (
              <Alert className={calculation.canProceed ? '' : 'border-red-500'}>
                <Calculator className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-2 mt-2">
                    <div className="flex justify-between">
                      <span>Gross Sale Amount:</span>
                      <span className="font-medium">
                        {InvestmentCalculator.formatCurrency(calculation.totalSaleAmount)}
                      </span>
                    </div>
                    <div className="flex justify-between text-red-600">
                      <span>Tax Withholding:</span>
                      <span className="font-medium">
                        -{InvestmentCalculator.formatCurrency(calculation.taxWithholding)}
                      </span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="font-medium">Net Proceeds:</span>
                      <span className="font-bold text-green-600">
                        {InvestmentCalculator.formatCurrency(calculation.netProceeds)}
                      </span>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Error Message */}
            {calculation && !calculation.canProceed && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{calculation.message}</AlertDescription>
              </Alert>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={formData.notes || ''}
                onChange={(e) => updateFormData({ notes: e.target.value })}
                placeholder="Reason for sale, special instructions, etc..."
                rows={3}
              />
            </div>

            {/* Important Notice */}
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Important:</strong> This transaction requires admin approval and may take 
                several days to process. You will be notified once approved. Tax implications 
                vary by jurisdiction - please consult your tax advisor.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={
                isPending || 
                calculating ||
                !formData.shares_to_sell ||
                !formData.equity_class_id ||
                !calculation?.canProceed
              }
            >
              {isPending ? 'Submitting...' : 'Submit Sale Request'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}